import { memo, useState } from "react";

import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  RowSelectionState,
  SortingState,
  useReactTable
} from "@tanstack/react-table";

import { PublishValidationResult } from "@/hooks/dataset-managment/useDatasetDataSetting";
import { DatasetAttributeSchema, DatasetMetadata } from "@/types/dataset";

import Pagination from "../Pagination";
import { DatasetDetailRow, ExtendedColumnDef } from "./columns";
import TableData from "./TableData";

const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_CURRENT_PAGE = 1;

type TableContainerProps = {
  data: DatasetDetailRow[];
  dynamicColumns: ExtendedColumnDef<DatasetDetailRow, unknown>[];
  metadata?: DatasetMetadata;
  highlightGeoColumns: DatasetAttributeSchema[];
  isHighlightNewGeometry: boolean;
  publishValidation: PublishValidationResult;
  setRowSelection: OnChangeFn<RowSelectionState>;
  rowSelection: RowSelectionState;
};

const TableContainer = ({
  data,
  dynamicColumns,
  metadata,
  highlightGeoColumns,
  isHighlightNewGeometry,
  publishValidation,
  rowSelection,
  setRowSelection
}: TableContainerProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_CURRENT_PAGE);

  const table = useReactTable<DatasetDetailRow>({
    data: data,
    columns: dynamicColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    pageCount: Math.ceil((metadata?.total_rows ?? 0) / pageSize),
    initialState: {
      pagination: {
        pageSize,
        pageIndex: currentPage - 1
      },
      columnPinning: {
        right: ["action"]
      },
      rowSelection: rowSelection
    },
    onSortingChange: (newSorting) => {
      setCurrentPage(1);
      setSorting(newSorting);
    },
    getSortedRowModel: getSortedRowModel(),
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    state: {
      pagination: {
        pageIndex: currentPage - 1,
        pageSize
      },
      sorting,
      rowSelection: rowSelection
    }
  });

  return (
    <>
      <TableData
        table={table}
        schema={metadata?.schema ?? []}
        highlightGeoColumns={highlightGeoColumns}
        isHighlightNewGeometry={isHighlightNewGeometry}
      />
      {publishValidation?.errors.referenceId && (
        <div className="text-cinnabar text-xs my-2">
          {publishValidation.errors.referenceId}
        </div>
      )}
      <Pagination
        table={table}
        pageSize={pageSize}
        setPageSize={setPageSize}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}
        totalData={metadata?.total_rows ?? 0}
      />
    </>
  );
};

// Custom comparison function
function arePropsEqual(
  prevProps: TableContainerProps,
  nextProps: TableContainerProps
) {
  // check row selection length
  if (prevProps.rowSelection !== nextProps.rowSelection) {
    return false;
  }
  // Check data length (quick check first)
  if (JSON.stringify(prevProps.data) !== JSON.stringify(nextProps.data)) {
    return false;
  }

  // Check highlight props
  if (
    prevProps.isHighlightNewGeometry !== nextProps.isHighlightNewGeometry ||
    prevProps.highlightGeoColumns.length !==
      nextProps.highlightGeoColumns.length
  ) {
    return false;
  }

  // Check essential metadata properties
  const metadataKeys = [
    "total_rows",
    "name",
    "id",
    "country_id",
    "spatial_aggregation_type",
    "published"
  ] as const;

  for (const key of metadataKeys) {
    if (prevProps.metadata?.[key] !== nextProps.metadata?.[key]) {
      return false;
    }
  }

  // Check schema reference properties
  if (prevProps.metadata?.schema && nextProps.metadata?.schema) {
    // If schema arrays have different lengths, they're not equal
    if (prevProps.metadata.schema.length !== nextProps.metadata.schema.length) {
      return false;
    }

    // Check important schema properties
    for (let i = 0; i < prevProps.metadata.schema.length; i++) {
      const prevSchema = prevProps.metadata.schema[i];
      const nextSchema = nextProps.metadata.schema[i];

      if (
        prevSchema.is_reference_geo !== nextSchema.is_reference_geo ||
        prevSchema.is_reference_id !== nextSchema.is_reference_id
      ) {
        return false;
      }
    }
  }

  // If we've passed all checks, the props are equal
  return true;
}

export default memo(TableContainer, arePropsEqual);
